import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { IconButton, Text, tokens } from '@hotjar/hotsauce';
import { useEffect, useRef } from 'react';

const Container = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  margin: ${tokens.spacer.x2pt5} auto 0;
  display: flex;
  padding-left: ${tokens.spacer.x2};
`;

const Label = styled(Text)`
  display: inline-flex;
  align-items: center;
  margin-right: ${tokens.spacer.x1};
`;

export const GeneralSurvey = ({
  onThumbsUpClick,
  onThumbsDownClick,
  onSurveyVisible,
}: {
  onThumbsUpClick: () => void;
  onThumbsDownClick: () => void;
  onSurveyVisible: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let observerRefValue: HTMLDivElement | null = null;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        onSurveyVisible();
        // prevent onSurveyVisible from being called multiple times
        // in case user is scrolling up and down
        if (containerRef.current) {
          observer.unobserve(containerRef.current);
        }
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
      observerRefValue = containerRef.current;
    }

    return () => {
      if (observerRefValue) {
        observer.unobserve(observerRefValue);
      }
    };
  }, [onSurveyVisible]);

  return (
    <Container ref={containerRef}>
      <Label textType="caption">{t('heatmaps:general-survey.title')}</Label>
      <IconButton
        size="s"
        kind="ghost"
        description={t('heatmaps:general-survey.thumbs-up-tooltip')}
        tooltipText={t('heatmaps:general-survey.thumbs-up-tooltip')}
        icon="thumbs_up"
        onClick={onThumbsUpClick}
      />
      <IconButton
        size="s"
        kind="ghost"
        description={t('heatmaps:general-survey.thumbs-down-tooltip')}
        tooltipText={t('heatmaps:general-survey.thumbs-down-tooltip')}
        icon="thumbs_down"
        onClick={onThumbsDownClick}
      />
    </Container>
  );
};
